import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { useBrand } from "../brand/BrandContext";
import { CornerLeaf } from "../components/CornerLeaf";

export function Cover() {
  const { variant } = useBrand();

  return (
    <div className="relative min-h-screen overflow-hidden px-7 md:px-14">
      {/* Hero photograph faded behind the headline — adds warmth and
          a sense of place. Sepia-tilted, low opacity, ken-burns. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-[68vh] overflow-hidden md:h-[80vh]"
        aria-hidden
      >
        <img
          src="/imagery/in_project_meditation_nature.jpg"
          alt=""
          className="h-full w-full object-cover [animation:kenburns_30s_ease-in-out_infinite_alternate]"
          style={{ filter: "sepia(0.22) saturate(0.85) brightness(0.92)" }}
        />
        {/* Cream wash so type stays legible over photography */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(230,207,154,0.92) 0%, rgba(230,207,154,0.72) 30%, rgba(230,207,154,0.86) 70%, var(--bg) 100%)",
          }}
        />
        {/* Floor warmth */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(160,90,31,0.28) 0%, rgba(160,90,31,0.06) 30%, transparent 55%)",
          }}
        />
      </div>

      <CornerLeaf position="right" top={24} size={88} />
      <CornerLeaf position="left" top={420} size={64} />

      <div className="relative mx-auto max-w-[920px] pt-28 pb-32 md:pt-40">
        <p
          className="font-flair text-[28px] text-amber"
          style={{ transform: "rotate(-2deg)", display: "inline-block" }}
        >
          something quieter
        </p>

        <motion.h1
          className="display mt-6 text-ink"
          style={{ fontSize: "clamp(3rem, 9vw, 7.4rem)" }}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, ease: [0.2, 0.05, 0.2, 1] }}
        >
          The brand book for
          <br />
          <em
            className="italic"
            style={{ color: "var(--amber)", fontStyle: "italic" }}
          >
            Vihangam Yog Australia.
          </em>
        </motion.h1>

        <motion.p
          className="mt-10 max-w-[640px] font-body text-[19px] leading-relaxed text-ink-soft"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          The brand for Vihangam Yog Australia — how we look, how we sound, and
          the small details we use across the website, the 11-Day Meditation
          Challenge, the Sunday Meetup, and every email we send. Treat it as a
          reference, not a rulebook.
        </motion.p>

        <motion.div
          className="mt-12 grid gap-4 md:grid-cols-3"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
        >
          <a
            href="#variants"
            className="surface p-5 transition-transform hover:-translate-y-0.5"
            style={{ background: "var(--paper)" }}
          >
            <p className="eyebrow">Three variants</p>
            <p className="mt-3 font-display text-[28px] italic text-amber">
              Calm ·
              <br />
              Grounded ·
              <br />
              Bold
            </p>
            <p className="mt-3 font-body text-[13px] leading-relaxed text-ink-soft">
              Toggle in the bottom-right corner to feel the difference.
              <br />
              <span className="text-ink">
                <em className="italic text-amber">Grounded</em> is what the live
                website + campaigns actually use today.
              </span>{" "}
              You're reading the{" "}
              <em className="italic text-amber">{variant.name}</em> variant.
            </p>
          </a>
          <a
            href="#how"
            className="surface p-5 transition-transform hover:-translate-y-0.5"
            style={{ background: "var(--paper)" }}
          >
            <p className="eyebrow">How to use it</p>
            <p className="mt-3 font-display text-[22px] text-ink">
              Read the sections in order at least once. After that, jump freely.
              When in doubt, defer to{" "}
              <em className="italic text-amber">Voice & Foundation.</em>
            </p>
          </a>
          <a
            href="#contact"
            className="surface p-5 transition-transform hover:-translate-y-0.5"
            style={{ background: "var(--paper)" }}
          >
            <p className="eyebrow">Who to ask</p>
            <p className="mt-3 font-display text-[22px] text-ink">
              For new applications, brand questions, or asset requests, write to{" "}
              <em className="italic text-amber">info@vyaustralia.org</em>.
            </p>
          </a>
        </motion.div>

        <div id="variants" className="mt-24">
          <p className="eyebrow">About this variant</p>
          <span className="hairline" />
          <p className="mt-6 max-w-[640px] font-body text-[16px] leading-relaxed text-ink-soft">
            {variant.longForm}
          </p>
        </div>

        <div id="how" className="mt-20">
          <p className="eyebrow">Reading order</p>
          <span className="hairline" />
          <ol className="mt-6 grid gap-2 font-body text-[15px] text-ink md:grid-cols-2">
            {[
              { n: "01", t: "Foundation", to: "/foundation" },
              { n: "02", t: "Voice & language", to: "/voice" },
              { n: "03", t: "Logo system", to: "/logo" },
              { n: "04", t: "Typography", to: "/typography" },
              { n: "05", t: "Colour", to: "/colour" },
              { n: "06", t: "Imagery", to: "/imagery" },
              { n: "07", t: "Motifs & motion", to: "/motifs" },
              { n: "08", t: "Applications", to: "/applications" },
              { n: "09", t: "Use cases", to: "/use-cases" },
            ].map((s) => (
              <li key={s.n}>
                <Link
                  to={s.to}
                  className="group flex items-baseline gap-3 border-b py-3 transition-colors border-rule hover:text-amber"
                >
                  <span className="font-display text-[15px] tabular-nums text-amber">
                    {s.n}
                  </span>
                  <span>{s.t}</span>
                  <span className="ml-auto opacity-0 transition-opacity group-hover:opacity-100">
                    →
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}
